import { useMemo, useRef } from "react"
import { offlineDb } from "../../../lib/offline/db"
import { PRE_ORDER_FLOW_TABLE } from "../../../lib/offline/resources/orders/pre-order-flow/db"
import { useSharedLiveQuery } from "../../../lib/offline/use-shared-live-query"

export interface ProductBreakdownEntry {
  productId: string
  /** Sorted, distinct pre-order IDs (within the input scope) whose flows
   *  reference this product. */
  preOrderIds: string[]
  /** Max of `flow.updatedAt` over the in-scope flows for this product. */
  latestUpdatedAt: string | null
  /** Min of `flow.updatedAt` over the in-scope flows for this product. */
  oldestUpdatedAt: string | null
  /** Max of `flow.createdAt` over the in-scope flows for this product. */
  latestCreatedAt: string | null
  /** Min of `flow.createdAt` over the in-scope flows for this product. */
  oldestCreatedAt: string | null
}

interface ProductAggregate {
  preOrderIds: Set<string>
  latestUpdatedAt: string | null
  oldestUpdatedAt: string | null
  latestCreatedAt: string | null
  oldestCreatedAt: string | null
}

/**
 * Stable empty-state reference. Returned from the hook whenever the
 * underlying observable has no value yet (first emit pending, or scope
 * just changed). Sharing one constant keeps downstream `useMemo` deps
 * stable so consumers don't re-render on transient empty states.
 */
const EMPTY_BREAKDOWN: ProductBreakdownEntry[] = []

/**
 * Reactive product → pre-order breakdown for the recap product view.
 *
 * Given the date-scoped pre-order set (already filtered by status / source /
 * employee / etc. at the headers level), return:
 *   - the distinct products appearing in any flow under those POs;
 *   - for each, the subset of in-scope POs that contain it; and
 *   - per-product min/max of `flow.updatedAt` and `flow.createdAt` so the
 *     recap can sort by the same flow-time criteria the legacy central
 *     pipeline supported (`latest-updated` / `oldest-updated` /
 *     `latest-created` / `oldest-created`).
 *
 * Implementation: two `uniqueKeys()` walks over the v32 compound indexes
 * `[preOrderId+productId+updatedAt]` and `[preOrderId+productId+createdAt]`
 * — one indexed range scan per PO per index, returning index-only tuples
 * without materialising flow rows. Aggregates are computed in-JS from the
 * tuples (string-compare on ISO timestamps; lexicographic order matches
 * chronological for ISO-8601). ~250 ms total for a 1,250-PO day vs ~1.5 s
 * for the legacy full-flow `fetchJunctions` scan, and the result drives
 * downstream `ProductGroup` mounts + sort comparators without ever loading
 * a flow row at the recap level.
 *
 * Reactive: re-runs on any `preOrderFlows` write. Output entries are
 * content-stable across re-fires so `ProductListView` / `ProductGroup`
 * memoization bails when nothing relevant moved.
 */
export function useProductBreakdownForPreOrders(
  preOrderIds: string[]
): ProductBreakdownEntry[] {
  const idsKey = useMemo(
    () => preOrderIds.slice().sort().join(","),
    [preOrderIds]
  )

  const raw = useSharedLiveQuery<ProductBreakdownEntry[]>(
    `productBreakdown:${idsKey}`,
    async () => {
      if (preOrderIds.length === 0) return []
      const table = offlineDb.table(PRE_ORDER_FLOW_TABLE)
      // Two index walks in parallel — one per timestamp field. Each returns
      // `[preOrderId, productId, timestamp]` triples for the PO's flows; we
      // collapse them into a single product-keyed aggregate.
      //
      // Scaling note: cost is `O(preOrderIds.length * 2)` range scans.
      // IndexedDB is single-threaded per origin so the `Promise.all` fan-
      // out serialises internally — ~250 ms on a 1 250-PO day, would push
      // toward 600 ms at 3 000 POs. The shared-cache layer keeps this to
      // once per unique scope (and reuses across remounts inside the
      // grace window), so most renders pay zero. If a tenant ever pushes
      // PO count high enough that this becomes the bottleneck, the next
      // step is a single multi-point scan via Dexie's `where("...").
      // anyOf(...)` — that requires switching the schema to a single
      // compound `[preOrderId+productId+updatedAt+createdAt]` (since
      // `anyOf` over partial compound keys isn't supported) and the
      // attendant DB version bump + reindex; not worth it today.
      const [updatedTuples, createdTuples] = await Promise.all([
        Promise.all(
          preOrderIds.map((id) =>
            table
              .where("[preOrderId+productId+updatedAt]")
              .between([id, "", ""], [id, "￿", "￿"])
              .uniqueKeys()
          )
        ),
        Promise.all(
          preOrderIds.map((id) =>
            table
              .where("[preOrderId+productId+createdAt]")
              .between([id, "", ""], [id, "￿", "￿"])
              .uniqueKeys()
          )
        ),
      ])

      const byProduct = new Map<string, ProductAggregate>()
      const ensure = (productId: string): ProductAggregate => {
        let agg = byProduct.get(productId)
        if (!agg) {
          agg = {
            preOrderIds: new Set<string>(),
            latestUpdatedAt: null,
            oldestUpdatedAt: null,
            latestCreatedAt: null,
            oldestCreatedAt: null,
          }
          byProduct.set(productId, agg)
        }
        return agg
      }
      for (const tuples of updatedTuples) {
        for (const tuple of tuples) {
          const [poId, productId, updatedAt] = tuple as unknown as [
            string,
            string,
            string,
          ]
          if (!productId) continue
          const agg = ensure(productId)
          agg.preOrderIds.add(poId)
          if (updatedAt) {
            if (!agg.latestUpdatedAt || updatedAt > agg.latestUpdatedAt) {
              agg.latestUpdatedAt = updatedAt
            }
            if (!agg.oldestUpdatedAt || updatedAt < agg.oldestUpdatedAt) {
              agg.oldestUpdatedAt = updatedAt
            }
          }
        }
      }
      for (const tuples of createdTuples) {
        for (const tuple of tuples) {
          const [poId, productId, createdAt] = tuple as unknown as [
            string,
            string,
            string,
          ]
          if (!productId) continue
          // `ensure` rather than `get` — the createdAt index will sometimes
          // surface a flow that the updatedAt walk also saw (the common
          // case), but in principle a flow could be indexed under one and
          // not the other if a write is mid-flight; staying defensive keeps
          // the breakdown coherent.
          const agg = ensure(productId)
          agg.preOrderIds.add(poId)
          if (createdAt) {
            if (!agg.latestCreatedAt || createdAt > agg.latestCreatedAt) {
              agg.latestCreatedAt = createdAt
            }
            if (!agg.oldestCreatedAt || createdAt < agg.oldestCreatedAt) {
              agg.oldestCreatedAt = createdAt
            }
          }
        }
      }

      const entries: ProductBreakdownEntry[] = []
      for (const [productId, agg] of byProduct) {
        entries.push({
          productId,
          preOrderIds: [...agg.preOrderIds].sort(),
          latestUpdatedAt: agg.latestUpdatedAt,
          oldestUpdatedAt: agg.oldestUpdatedAt,
          latestCreatedAt: agg.latestCreatedAt,
          oldestCreatedAt: agg.oldestCreatedAt,
        })
      }
      entries.sort((a, b) => a.productId.localeCompare(b.productId))
      return entries
    }
  )

  // Hold previous output and reuse entries that are content-equal so
  // downstream React.memo bails when a flow write doesn't change the
  // (productId, preOrderIds, timestamps) shape — only the affected
  // product's entry gets a fresh reference.
  //
  // Scope-change safety: `lastRef` is keyed by `idsKey` so a different
  // `preOrderIds` set never returns the previous scope's breakdown while
  // the new scope's data is still resolving. `EMPTY_BREAKDOWN` is a
  // stable module-level constant so the empty-state reference is also
  // shared across renders.
  const lastKeyRef = useRef<string>(idsKey)
  const lastRef = useRef<ProductBreakdownEntry[]>(EMPTY_BREAKDOWN)
  return useMemo(() => {
    if (lastKeyRef.current !== idsKey) {
      lastKeyRef.current = idsKey
      lastRef.current = EMPTY_BREAKDOWN
    }
    if (!raw) return EMPTY_BREAKDOWN
    const prev = lastRef.current
    if (raw.length === prev.length) {
      let allSame = true
      const next: ProductBreakdownEntry[] = new Array(raw.length)
      for (let i = 0; i < raw.length; i++) {
        const a = raw[i]!
        const b = prev[i]!
        if (
          a.productId === b.productId &&
          a.latestUpdatedAt === b.latestUpdatedAt &&
          a.oldestUpdatedAt === b.oldestUpdatedAt &&
          a.latestCreatedAt === b.latestCreatedAt &&
          a.oldestCreatedAt === b.oldestCreatedAt &&
          a.preOrderIds.length === b.preOrderIds.length &&
          a.preOrderIds.every((id, j) => id === b.preOrderIds[j])
        ) {
          next[i] = b
        } else {
          allSame = false
          next[i] = a
        }
      }
      if (allSame) return prev
      lastRef.current = next
      return next
    }
    lastRef.current = raw
    return raw
  }, [raw])
}
